import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, ChevronDown, ShoppingCart, Star, Mail, LogOut, Tag } from 'lucide-react';
import { TickingClock } from '../ui/TickingClock';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/PortalContext';
import { useAuth } from '../../context/AuthContext';
import { useNotificationsOptional } from '../../context/NotificationsContext';
import { useAdminNotificationsOptional } from '../../context/AdminNotificationsContext';
import { SearchCodici } from './SearchCodici';
import { SearchModelli } from './SearchModelli';
import { BudgetNavBadge } from './BudgetNavBadge';

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href?: string;
  children?: NavChild[];
  highlight?: boolean;
}

interface HeaderProps {
  onOpenSearch?: () => void;
}

const SUPPORTO_ITEM: NavItem = {
  label: 'Supporto',
  children: [
    { label: 'Risorse & Modulistica', href: '/supporto/risorse' },
    { label: 'Reso Prodotti', href: '/supporto/resi' },
    { label: 'Catalogo Online', href: '/supporto/catalogo-online' },
    { label: 'FAQ', href: '/supporto/faq' },
  ]
};

const AZIENDA_ITEM: NavItem = {
  label: 'Azienda',
  href: '/azienda/chi-siamo',
  children: [
    { label: 'Chi Siamo', href: '/azienda/chi-siamo' },
    { label: 'La Nostra Mission', href: '/azienda/mission' },
    { label: 'Logistica DTK', href: '/azienda/disponibilita-logistica' },
    { label: 'Area Commerciale', href: '/azienda/area-commerciale' },
    { label: 'Lavora con Noi', href: '/azienda/lavora-con-noi' },
  ]
};

const KTEK_ITEM: NavItem = {
  label: 'KTEK',
  children: [
    { label: 'Panoramica KTEK', href: '/ktek' },
    { label: 'Ktek PRO', href: '/ktek/pro' },
    { label: 'Ktek SMART', href: '/ktek/smart' },
  ]
};

// Navbar da NON loggato: "Prodotti" è un link semplice alla pagina di
// panoramica categorie (`/prodotti/tutti`), che mostra tutte le sezioni
// come card (nessun menu a tendina in navbar, su richiesta utente).
// "KTEK" è il marchio proprietario DTK (linee PRO/SMART), con menu a
// tendina come "Azienda"/"Supporto".
// "Azienda" è la prima voce su richiesta utente; "Outlet" non è più in
// questo elenco — viene renderizzato separatamente, spostato a destra e in
// risalto (vedi sezione "Layer 2" più sotto).
const NAV_ITEMS_LOGGED_OUT: NavItem[] = [
  { label: 'Home', href: '/' },
  AZIENDA_ITEM,
  { label: 'Prodotti', href: '/i-nostri-prodotti' },
  KTEK_ITEM,
  { label: 'Attrezzature tecniche', href: '/prodotti/prodotti-pulizia' },
  { label: 'Offerte', href: '/offerte', highlight: true },
  SUPPORTO_ITEM,
  { label: 'Contatti', href: '/contatti' },
];

// Navbar da loggato: versione ridotta su richiesta utente — niente mega-menu
// "Prodotti" (si naviga via ricerca/search bar), Home esplicito, Supporto
// spostato tra Offerte e Contatti. "Home" prima voce, poi "Area Cliente"
// (voce aggiunta su richiesta utente 21/02/2026, porta all'area personale
// in base al ruolo: cliente e agente → /area-clienti, admin → /admin),
// poi "Azienda" (ordine invertito su richiesta utente 10/07/2026), "Outlet" a parte.
function getNavItemsLoggedIn(areaHref: string): NavItem[] {
  return [
    { label: 'Home', href: '/' },
    { label: 'Area Cliente', href: areaHref },
    AZIENDA_ITEM,
    { label: 'Attrezzature tecniche', href: '/prodotti/prodotti-pulizia' },
    { label: 'Offerte', href: '/offerte', highlight: true },
    SUPPORTO_ITEM,
    { label: 'Contatti', href: '/contatti' },
  ];
}

export function Header({ onOpenSearch }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { totalItems, setIsOpen: openCart } = useCart();
  const { favorites } = useFavorites();
  const { user, logout, isAuthenticated } = useAuth();
  const notifCtx = useNotificationsOptional();
  const adminNotifCtx = useAdminNotificationsOptional();
  // Badge e destinazione della campanella dipendono dal ruolo
  const isAdmin = user?.ruolo === 'admin';
  const isAdminOperatore = user?.ruolo === 'admin_operatore';
  const unreadNotifs = isAdmin
    ? (adminNotifCtx?.data?.unreadCount ?? 0)
    : (notifCtx?.data?.unreadCount ?? 0);
  const bellHref = isAdmin ? '/admin/notifiche' : isAdminOperatore ? '/admin' : '/area-clienti/avvisi';
  // Area personale: destinazione in base al ruolo (riusata anche dal
  // pulsante icona utente in alto a destra).
  const areaHref = isAdmin || isAdminOperatore ? '/admin' : '/area-clienti';
  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const NAV_ITEMS = isAuthenticated ? getNavItemsLoggedIn(areaHref) : NAV_ITEMS_LOGGED_OUT;
  // "/" andrebbe sempre in match con startsWith: forziamo l'uguaglianza esatta
  // per "Home", altrimenti risulterebbe sempre evidenziato su qualsiasi pagina.
  const isNavActive = (href: string) => href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  // Chiude sempre il menu mobile dopo una navigazione (es. click su un
  // risultato di SearchCodici/SearchModelli, che non ha accesso a setMobileOpen).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    logout();
    navigate('/accedi');
  };

  const handleEnter = (label: string) => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setOpenDropdown(label);
  };

  const handleLeave = () => {
    leaveTimer.current = setTimeout(() => setOpenDropdown(null), 180);
  };

  return (
    <header
      className="sticky z-50 no-print"
      style={{ top: 'var(--impersonation-banner-h, 0px)' }}
      data-testid="site-header"
    >

      {/* ── Layer 1: White — Logo + Search + Actions ─────────────────── */}
      <div className="bg-white border-b">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="h-16 flex items-center gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
              <img
                src="/logo.png"
                alt="DTK Imaging Supplies"
                className="h-7 w-auto object-contain"
              />
            </Link>

            {/* Search Bars — center */}
            <div className="hidden lg:flex flex-1 items-center gap-3" data-testid="header-search-bar">
              <SearchCodici />
              <SearchModelli />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 ml-auto flex-shrink-0">
              {isAuthenticated && (
                <>
                  <Link to="/area-clienti/preferiti" className="hidden sm:flex relative h-9 w-9 rounded-full items-center justify-center text-gray-500 hover:text-[#2B5EA7] hover:bg-gray-100 transition-colors" aria-label="Preferiti">
                    <Star className="w-[18px] h-[18px]" />
                    {favorites.length > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] px-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">{favorites.length}</span>}
                  </Link>
                  <Link to={bellHref} className="hidden sm:flex relative h-9 w-9 rounded-full items-center justify-center text-gray-500 hover:text-[#2B5EA7] hover:bg-gray-100 transition-colors" aria-label="Messaggi">
                    <Mail className="w-[18px] h-[18px]" />
                    {unreadNotifs > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{unreadNotifs}</span>}
                  </Link>
                  <button
                    onClick={() => openCart(true)}
                    className="relative h-10 w-10 rounded-xl flex items-center justify-center bg-[#007B5E] text-white hover:bg-[#006B4F] transition-colors"
                    data-testid="cart-open-button"
                    aria-label="Apri carrello"
                  >
                    <ShoppingCart className="w-[18px] h-[18px]" />
                    {totalItems > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-[#007B5E] text-[10px] font-bold flex items-center justify-center border border-[#007B5E]" data-testid="cart-badge">
                        {totalItems}
                      </span>
                    )}
                  </button>
                </>
              )}
              {isAuthenticated ? (
                <div className="flex items-center gap-0 ml-1">
                  <Link
                    to={areaHref}
                    className="h-9 px-3 rounded-full lg:rounded-l-full lg:rounded-r-none bg-[#2B5EA7] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#1E4D8C] transition-colors"
                    data-testid="user-area-link"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline max-w-[100px] truncate">{user?.nome || user?.email}</span>
                  </Link>
                  {/* Su mobile il logout non sta nella navbar: era un
                      pulsante solo-icona, distruttivo, appiccicato al link
                      dell'area cliente. Vive nel menu (in fondo, in un
                      blocco separato) e nel drawer dell'Area Cliente. */}
                  <button
                    onClick={handleLogout}
                    className="hidden lg:flex h-9 w-9 rounded-r-full bg-[#2B5EA7] text-white items-center justify-center hover:bg-red-600 transition-colors border-l border-[#1E4D8C]"
                    title="Disconnetti"
                    aria-label="Disconnetti"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-1">
                  <Link
                    to="/accedi"
                    className="h-9 px-4 rounded-full bg-[#2B5EA7] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#1E4D8C] transition-colors"
                    data-testid="login-link"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">Accedi</span>
                  </Link>
                  <Link
                    to="/registrati"
                    className="group relative h-9 px-4 rounded-full bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] text-sm font-bold flex items-center gap-1.5 overflow-hidden hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] shadow-sm hover:shadow-md transition-all"
                    data-testid="register-link"
                  >
                    <span>Registrati</span>
                    <span
                      className="absolute -inset-y-0 -inset-x-1 -skew-x-12 bg-white/30 w-6 opacity-0 group-hover:opacity-100 group-hover:translate-x-[160%] transition-all duration-700 ease-out"
                      aria-hidden
                    />
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden h-11 w-11 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-panel"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Layer 2: Blue Nav Bar ──────────────────────────────────────── */}
      <div className="hidden lg:block bg-[#2B5EA7]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="h-11 flex items-center gap-1" data-testid="desktop-nav">
            {NAV_ITEMS.map(item => (
              <div key={item.label} className="relative"
                onMouseEnter={() => item.children && handleEnter(item.label)}
                onMouseLeave={handleLeave}
              >
                {item.children ? (
                  item.href ? (
                    <Link
                      to={item.href}
                      onClick={() => setOpenDropdown(null)}
                      data-testid={`primary-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                        openDropdown === item.label ? 'text-white bg-white/20' : 'text-white hover:bg-white/10'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </Link>
                  ) : (
                    <button
                      data-testid={`primary-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                        openDropdown === item.label ? 'text-white bg-white/20' : 'text-white hover:bg-white/10'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>
                  )
                ) : item.highlight ? (
                  <Link
                    to={item.href!}
                    data-testid={`primary-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`group relative px-3.5 py-1.5 text-sm font-bold rounded-md transition-all flex items-center gap-1.5 overflow-hidden ${
                      isNavActive(item.href!)
                        ? 'bg-amber-400 text-[#1E4D8C] shadow-md'
                        : 'bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] hover:from-amber-400 hover:to-amber-500 hover:scale-[1.04] shadow-sm hover:shadow-md'
                    }`}
                  >
                    <TickingClock className="w-5 h-5" />
                    <span className="tracking-wide">{item.label}</span>
                    <span
                      className="absolute -inset-y-0 -inset-x-1 -skew-x-12 bg-white/30 w-6 opacity-0 group-hover:opacity-100 group-hover:translate-x-[120%] transition-all duration-700 ease-out"
                      aria-hidden
                    />
                  </Link>
                ) : (
                  <Link
                    to={item.href!}
                    data-testid={`primary-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors text-white hover:bg-white/10"
                  >
                    {item.label}
                  </Link>
                )}

                {/* Dropdown (Supporto / Azienda) */}
                {item.children && openDropdown === item.label && (
                  <div className="absolute top-full left-0 pt-2 z-[100]">
                    <div className="w-56 bg-white rounded-xl border shadow-lg py-1.5">
                      {item.children.map(child => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#F2F2F2] transition-colors"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Budget cliente (miniatura, richiesta utente 21/02/2026) + Outlet,
                raggruppati con un unico ml-auto cosi l'allineamento a destra
                resta corretto anche quando il badge Budget non è visibile
                (ospiti o budget non abilitato). */}
            <div className="ml-auto flex items-center gap-1">
              {isAuthenticated && <BudgetNavBadge />}
              <Link
                to="/outlet"
                data-testid="primary-nav-outlet"
                className={`group flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md border transition-colors hover:bg-amber-400 hover:border-amber-400 hover:text-[#1E4D8C] ${
                  location.pathname.startsWith('/outlet') ? 'bg-amber-400 border-amber-400 text-[#1E4D8C]' : 'border-white/40 text-white'
                }`}
              >
                <Tag className={`w-4 h-4 fill-current transition-colors ${
                  location.pathname.startsWith('/outlet') ? 'text-[#1E4D8C]' : 'text-amber-400 group-hover:text-[#1E4D8C]'
                }`} />
                <span>Outlet</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* -- Menu mobile ------------------------------------------------
          Prima era un `<div>` statico **in flusso**: apriva spingendo giu
          la pagina, portava il documento a 3.404px, non aveva scroll
          proprio (nessun `max-height`), nessun overlay, nessun z-index,
          nessuno scroll-lock, ne Escape o click fuori. Essendo l'header
          `sticky`, su viewport corto lo rendeva piu alto dello schermo e le
          ultime voci diventavano irraggiungibili.

          Ora e un pannello Radix come il drawer dell'Area Cliente: focus
          trap, scroll-lock, Escape e ripristino del focus arrivano da li.
          Entra da destra, dove sta l'hamburger che lo apre.

          Il `Dialog.Root` e controllato e senza `Trigger`: il pulsante
          resta dov'e nella navbar, e Radix riporta comunque il focus
          all'elemento che l'aveva prima dell'apertura. */}
      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 z-[95] bg-[rgb(15_23_42/0.55)] lg:hidden data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none"
            data-testid="mobile-menu-overlay"
          />
          <Dialog.Content
            id="mobile-menu-panel"
            className="fixed inset-y-0 right-0 z-[95] flex w-[min(92vw,380px)] flex-col bg-white shadow-[-4px_0_24px_rgb(0_0_0/0.18)] [overscroll-behavior:contain] duration-200 lg:hidden data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right motion-reduce:animate-none motion-reduce:duration-0"
            data-testid="mobile-menu"
          >
            <div className="flex flex-none items-center justify-between border-b px-4 py-3">
              <Dialog.Title className="text-base font-semibold">Menu</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="-mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-gray-100"
                  aria-label="Chiudi menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="sr-only">
              Ricerca rapida e navigazione del sito
            </Dialog.Description>

            {/* L'unica area di scroll del pannello. */}
            <div className="flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch] px-4 pb-4">
              <div className="flex flex-col gap-2 pt-3">
                <SearchCodici placeholder="Ricerca Rapida Codici" />
                <SearchModelli placeholder="Ricerca Rapida Modelli" />
              </div>

              {/* Outlet - in risalto anche su mobile: solo bordo, si colora di giallo al tocco/hover */}
              <Link
                to="/outlet"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-outlet"
                className="mt-3 flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-[#2B5EA7] text-[#2B5EA7] text-base font-bold hover:border-amber-400 hover:bg-amber-400 hover:text-[#1E4D8C] transition-colors"
              >
                <Tag className="w-[18px] h-[18px]" /> Outlet
              </Link>

              {isAuthenticated && <div className="mt-3"><BudgetNavBadge mobile /></div>}
              {/* `sm:hidden`: da 640px le stesse icone (preferiti, messaggi)
                  compaiono nell'header, quindi qui sarebbero un doppione.
                  Era così anche prima di questa riscrittura. */}
              {isAuthenticated && (
                <div className="mt-3 flex sm:hidden items-center gap-2 border-b pb-3">
                  <Link to="/area-clienti/preferiti" onClick={() => setMobileOpen(false)} className="tap flex-1 flex items-center justify-center gap-1.5 h-11 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[13px] font-semibold" data-testid="mobile-favorites-link">
                    <Star className="w-4 h-4" /> Preferiti {favorites.length > 0 && `(${favorites.length})`}
                  </Link>
                  <Link to={bellHref} onClick={() => setMobileOpen(false)} className="tap flex-1 flex items-center justify-center gap-1.5 h-11 rounded-lg bg-gray-50 border text-gray-700 text-[13px] font-semibold" data-testid="mobile-notifications-link">
                    <Mail className="w-4 h-4" /> Messaggi {unreadNotifs > 0 && `(${unreadNotifs})`}
                  </Link>
                </div>
              )}

              {/* I sottomenu erano sempre espansi: 7 voci piu ~12 figli
                  renderizzati tutti insieme. Ora sono `<details>` chiusi -
                  nessun JS, accessibili per default - e si apre solo
                  quello che serve. */}
              <nav className="pt-2" aria-label="Navigazione principale">
                {NAV_ITEMS.map(item => (
                  <div key={item.label}>
                    {item.children ? (
                      <details className="group border-b last:border-b-0 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="tap flex cursor-pointer list-none items-center justify-between px-3 py-2.5 text-sm font-medium">
                          {item.label}
                          <ChevronDown className="h-4 w-4 flex-none text-gray-500 transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
                        </summary>
                        <div className="pb-2">
                          {item.href && (
                            <Link to={item.href} onClick={() => setMobileOpen(false)} className="tap flex items-center px-6 py-2.5 rounded-lg text-sm text-gray-600 hover:bg-[#F2F2F2]">
                              Panoramica {item.label}
                            </Link>
                          )}
                          {item.children.map(child => (
                            <Link key={child.href} to={child.href} onClick={() => setMobileOpen(false)} className="tap flex items-center px-6 py-2.5 rounded-lg text-sm hover:bg-[#F2F2F2]">{child.label}</Link>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <Link to={item.href!} onClick={() => setMobileOpen(false)} className="tap flex items-center border-b last:border-b-0 px-3 py-2.5 text-sm font-medium hover:bg-[#F2F2F2]">{item.label}</Link>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Identita + logout, fuori dall'area di scroll e separati
                dalla navigazione: il logout non deve stare a filo delle
                voci di menu. Sostituisce il pulsante icona della navbar. */}
            {isAuthenticated && (
              <div
                className="flex-none border-t px-4 py-3"
                style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
              >
                <p className="truncate text-sm font-semibold">{user?.nome || user?.email}</p>
                <button
                  type="button"
                  onClick={() => { setMobileOpen(false); handleLogout(); }}
                  data-testid="mobile-logout"
                  className="mt-2 flex min-h-[44px] w-full items-center gap-2 rounded-lg text-sm text-[#B42318] hover:bg-rose-50"
                >
                  <LogOut className="h-4 w-4 flex-none" aria-hidden="true" /> Esci
                </button>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </header>
  );
}
